import useAllPaymentMethodsQuery from 'hooks/useAllPaymentMethodsQuery'
import useNestedDocuments from 'hooks/useNestedDocuments'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import getFirstLevelLocation from 'utils/getFirstLevelLocation'
import { deepMarkCurrentItems } from 'utils/markCurrentItem'
import { normalisePath } from '../utils/prefix'
import { useSdkItems } from './useSdkItems'

const traverseTree = item => {
	if (item.smallText) {
		return {
			type: 'SMALL_TEXT',
			smallText: item.smallText,
		}
	}

	if (!item.title || item.hidden) {
		return
	}

	let subItems = []
	if (item.items.length) {
		subItems = getSubMenuItems(item.items).filter(Boolean)
	}

	return {
		type: 'ITEM',
		titleImg: item.icon?.publicURL,
		title: item.title,
		url: item.url,
		items: subItems,
		active: item.current,
	}
}

const getSubMenuItems = subMenuItems => {
	const secondaryMenuItems = []

	subMenuItems.forEach(item => {
		if (item.divider) {
			secondaryMenuItems.push({
				type: 'SEPARATOR',
			})
		}

		const subItem = traverseTree(item)
		subItem && secondaryMenuItems.push(subItem)
	})

	return secondaryMenuItems
}

const getSecondaryMenuItems = subMenuItems => {
	const secondaryMenuItems = []

	let currentSection

	subMenuItems.forEach(item => {
		if (item.divider) {
			if (currentSection) {
				currentSection.items.push({ type: 'SEPARATOR' })
			} else {
				secondaryMenuItems.push({
					type: 'SEPARATOR',
				})
			}
			// currentSection = undefined
			// return
		}

		if (item.virtual && !item.items?.length) {
			return
		}

		if (!currentSection || item.section != currentSection?.title) {
			currentSection = {
				type: 'SECTION',
				title: item.section,
				items: [],
			}

			secondaryMenuItems.push(currentSection)
		}

		const subItem = traverseTree(item)
		subItem && currentSection.items.push(subItem)
	})

	return secondaryMenuItems
}

export default function useSubMenuItems(location) {
	const firstLevelLocation = getFirstLevelLocation(location)
	const nestedDocuments = useNestedDocuments(firstLevelLocation.pathname, {
		withoutParent: false,
	})
	const paymentMethods = useAllPaymentMethodsQuery()
	const { sdkItems } = useSdkItems()

	return useMemo(() => {
		const path = normalisePath(location.pathname)
		const [, root, platform, version] = path.split('/') ?? []

		if (root === 'sdk')
			return {
				platform,
				sdkItems,
				secondaryMenuItems: sdkItems[platform]?.versions[version],
				version,
			}

		let subMenuItems = [...nestedDocuments]

		if (root === 'payment-methods') {
			// Add a divider
			subMenuItems.push({ divider: true })

			// Put Cards first
			const cardsIndex = paymentMethods.findIndex(({ slug }) => slug === 'card')
			const cards = paymentMethods[cardsIndex]
			if (cards) {
				paymentMethods.splice(cardsIndex, 1)
				paymentMethods.unshift(cards)
			}

			// Add payment methods
			paymentMethods.forEach(paymentMethod => {
				if (['YourPaymentMethodName', 'DummyPaymentMethod'].includes(paymentMethod.name)) return

				subMenuItems.push({
					url: paymentMethod.url,
					icon: {
						publicURL: paymentMethod.icon,
					},
					title: paymentMethod.name,
					virtual: true, // For markCurrentItem
					items: [
						{
							url: paymentMethod.url,
							title: 'Overview',
							items: [],
						},

						...paymentMethod.processors.map(processor => ({
							url: `${paymentMethod.url}/${processor.slug}`,
							title: `↪ ${processor.directIntegration ? 'Direct Integration' : processor.name}`,
							virtual: true, // For markCurrentItem
							items: [],
						})),
					],
				})
			})

			subMenuItems.push({
				smallText: 'More payment methods guides are coming soon!',
			})
		}

		subMenuItems = deepMarkCurrentItems(location.pathname, subMenuItems)
		const secondaryMenuItems = getSecondaryMenuItems(subMenuItems)
		return { secondaryMenuItems }
	}, [location.pathname])
}
